import { KernBadge } from '@kern-ux-annex/kern-react-kit'
import { motion } from 'motion/react'
import { useId } from 'react'

import type { FeatureInfo } from '../api/types'

/** One function (feature toggle) as a switch – in the developer panel and on the platform. */
export function FeatureSwitch({
  feature,
  disabled,
  busy,
  onToggle,
}: {
  feature: FeatureInfo
  disabled: boolean
  busy: boolean
  onToggle: (enabled: boolean) => void
}) {
  const id = useId()
  return (
    <li className="dms-card flex items-start justify-between gap-16 p-12">
      <div className="flex flex-col gap-4">
        <label htmlFor={id} className="flex flex-wrap items-center gap-8 font-semibold">
          {feature.label}
          <KernBadge label={feature.workPackage} variant="info" />
        </label>
        <p className="kern-body kern-body--small dms-muted" id={`${id}-desc`}>
          {feature.description}
        </p>
      </div>
      <button
        id={id}
        type="button"
        role="switch"
        aria-checked={feature.enabled}
        aria-describedby={`${id}-desc`}
        disabled={disabled || busy}
        onClick={() => onToggle(!feature.enabled)}
        className="dms-focus relative mt-2 inline-flex h-28 w-48 shrink-0 items-center rounded-full border p-2 disabled:opacity-50"
        style={{
          background: feature.enabled ? 'var(--dms-action)' : 'var(--dms-surface-raised)',
          borderColor: feature.enabled ? 'var(--dms-action)' : 'var(--dms-border)',
        }}
      >
        <motion.span
          layout
          transition={{ type: 'spring', stiffness: 600, damping: 32 }}
          className="block size-20 rounded-full"
          style={{
            background: feature.enabled ? 'var(--dms-on-action)' : 'var(--dms-muted)',
            marginLeft: feature.enabled ? 'auto' : 0,
          }}
        />
        <span className="sr-only">{feature.enabled ? 'an' : 'aus'}</span>
      </button>
    </li>
  )
}
